import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Skeleton,
  Avatar,
} from '@mui/material';

interface SkeletonLoaderProps {
  type: 'table' | 'cards' | 'statistics' | 'productGrid' | 'recentOrders' | 'topSellingProducts' | 'nearOutOfStockProducts' | 'monthWiseOrders';
  count?: number;
}

const SkeletonLoader: React.FC<SkeletonLoaderProps> = ({ type, count = 4 }) => {
  const renderTableSkeleton = () => (
    <Box>
      {/* Search and filter skeleton */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
            <Skeleton variant="rectangular" height={40} />
            <Skeleton variant="rectangular" height={40} />
          </Box>
        </CardContent>
      </Card>
      
      {/* Table skeleton */}
      <Card>
        <CardContent>
          {/* Table header */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 2, mb: 2 }}>
            <Skeleton variant="text" height={24} />
            <Skeleton variant="text" height={24} />
            <Skeleton variant="text" height={24} />
            <Skeleton variant="text" height={24} />
          </Box>
          
          {/* Table rows */}
          {Array.from({ length: count }).map((_, index) => (
            <Box key={index} sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 2, mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Skeleton variant="circular" width={40} height={40} />
                <Skeleton variant="text" width="60%" height={20} />
              </Box>
              <Skeleton variant="text" width="40%" height={20} />
              <Skeleton variant="text" width="30%" height={20} />
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Skeleton variant="circular" width={32} height={32} />
                <Skeleton variant="circular" width={32} height={32} />
                <Skeleton variant="circular" width={32} height={32} />
              </Box>
            </Box>
          ))}
        </CardContent>
      </Card>
    </Box>
  );

  const renderCardsSkeleton = () => (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' },
        gap: 2,
      }}
    >
      {Array.from({ length: count }).map((_, index) => (
        <Card key={index} sx={{ height: '100%' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Skeleton variant="circular" width={50} height={50} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="80%" height={24} />
                <Skeleton variant="text" width="40%" height={20} />
              </Box>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
              <Box>
                <Skeleton variant="text" width="60%" height={16} />
                <Skeleton variant="text" width="40%" height={24} />
              </Box>
              <Box>
                <Skeleton variant="text" width="60%" height={16} />
                <Skeleton variant="text" width="40%" height={24} />
              </Box>
            </Box>
          </CardContent>
        </Card>
      ))}
    </Box>
  );

  const renderStatisticsSkeleton = () => (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
        gap: 3,
        mb: 3,
      }}
    >
      {Array.from({ length: 3 }).map((_, index) => (
        <Card key={index} sx={{ 
          bgcolor: 'grey.100', 
          height: '100%',
          boxShadow: 3,
          '&:hover': {
            boxShadow: 6,
            transform: 'translateY(-2px)',
            transition: 'all 0.3s ease-in-out',
          },
        }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box mr={2}>
                <Skeleton variant="circular" width={40} height={40} />
              </Box>
              <Box>
                <Skeleton variant="text" width={80} height={48} sx={{ fontSize: '2rem', fontWeight: 'bold' }} />
                <Skeleton variant="text" width={120} height={20} sx={{ fontSize: '0.875rem' }} />
              </Box>
            </Box>
          </CardContent>
        </Card>
      ))}
    </Box>
  );

  const renderProductGridSkeleton = () => (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' },
        gap: 2,
      }}
    >
      {Array.from({ length: count }).map((_, index) => (
        <Card key={index} sx={{ height: '100%' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Skeleton variant="circular" width={50} height={50} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="80%" height={24} />
                <Skeleton variant="text" width="40%" height={20} />
              </Box>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
              <Box>
                <Skeleton variant="text" width="60%" height={16} />
                <Skeleton variant="text" width="40%" height={24} />
              </Box>
              <Box>
                <Skeleton variant="text" width="60%" height={16} />
                <Skeleton variant="text" width="40%" height={24} />
              </Box>
              <Box>
                <Skeleton variant="text" width="60%" height={16} />
                <Skeleton variant="text" width="40%" height={24} />
              </Box>
            </Box>
          </CardContent>
        </Card>
      ))}
    </Box>
  );

  const renderRecentOrdersSkeleton = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {Array.from({ length: count }).map((_, index) => (
        <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Skeleton variant="text" width="60%" height={20} />
            <Skeleton variant="text" width="40%" height={16} />
          </Box>
          <Skeleton variant="rectangular" width={60} height={24} sx={{ borderRadius: 1 }} />
        </Box>
      ))}
    </Box>
  );

  const renderTopSellingProductsSkeleton = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {Array.from({ length: count }).map((_, index) => (
        <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: '0.8rem' }}>
            <Skeleton variant="circular" width={32} height={32} />
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Skeleton variant="text" width="70%" height={20} />
            <Skeleton variant="text" width="50%" height={16} />
          </Box>
        </Box>
      ))}
    </Box>
  );

  const renderNearOutOfStockProductsSkeleton = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {Array.from({ length: count }).map((_, index) => (
        <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: '0.8rem' }}>
            <Skeleton variant="circular" width={32} height={32} />
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Skeleton variant="text" width="70%" height={20} />
            <Skeleton variant="text" width="50%" height={16} />
          </Box>
          <Skeleton variant="rectangular" width={60} height={24} sx={{ borderRadius: 1 }} />
        </Box>
      ))}
    </Box>
  );

  const renderMonthWiseOrdersSkeleton = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
        <Skeleton variant="rectangular" width="100%" height={60} sx={{ borderRadius: 1 }} />
        <Skeleton variant="rectangular" width="100%" height={60} sx={{ borderRadius: 1 }} />
        <Skeleton variant="rectangular" width="100%" height={60} sx={{ borderRadius: 1 }} />
      </Box>
    </Box>
  );

  switch (type) {
    case 'table':
      return renderTableSkeleton();
    case 'cards':
      return renderCardsSkeleton();
    case 'statistics':
      return renderStatisticsSkeleton();
    case 'productGrid':
      return renderProductGridSkeleton();
    case 'recentOrders':
      return renderRecentOrdersSkeleton();
    case 'topSellingProducts':
      return renderTopSellingProductsSkeleton();
    case 'nearOutOfStockProducts':
      return renderNearOutOfStockProductsSkeleton();
    case 'monthWiseOrders':
      return renderMonthWiseOrdersSkeleton();
    default:
      return <Skeleton variant="rectangular" height={200} />;
  }
};

export default SkeletonLoader; 